import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    isCompanionCapableSurface,
    isStaleCompanionDevice,
} from 'flow/companion/companion-utils';
import { selectDesignatedCompanionDevice } from 'flow/selectors/app-selectors';
import { getAppDownloadUrls } from 'flow/utils/app-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';

const DISMISSED_KEY = 'installDesktopAppBannerDismissed';

type Props = {
    className?: string;
};

/**
 * Offers the desktop app to a surface that has no companion to hand its calls to.
 *
 * Shown only where it would change something: on a surface that could delegate, when the account
 * has no desktop app designated or the one it has has not been seen in a fortnight. A user whose
 * companion is already working is told nothing, because there is nothing for them to do.
 *
 * Approximates "no desktop app on this computer", which is what the design asks for and what we
 * cannot yet see - a browser has no way to notice an Electron beside it. An account-wide answer is
 * the closest available, and errs towards silence: someone with a working companion elsewhere is
 * not nagged, someone with none is.
 */
const InstallDesktopAppBanner = ({ className }: Props) => {
    const { i18next } = useAppContext();

    const companionCapable = useAppSelector(isCompanionCapableSurface);
    const designated = useAppSelector(selectDesignatedCompanionDevice);
    const [dismissed, setDismissed] = useState(() =>
        wrappedLocalStorage.isSet(DISMISSED_KEY),
    );

    const dismiss = () => {
        wrappedLocalStorage.setItem(DISMISSED_KEY, 'true');
        setDismissed(true);
    };

    if (!companionCapable || dismissed) {
        return null;
    }

    if (designated && !isStaleCompanionDevice(designated)) {
        return null;
    }

    const downloadUrl = getAppDownloadUrls()?.website;
    if (!downloadUrl) {
        return null;
    }

    return (
        <div
            className={twMerge(
                'flex w-full items-center justify-between gap-x-3 bg-gray-800 px-3 py-2 text-xs text-white',
                className,
            )}
        >
            <span>{i18next.t('companion.promo.install')}</span>
            <div className="flex shrink-0 items-center gap-x-2">
                <a
                    className="hover:darken-10 rounded-sm bg-white px-2 py-1 font-bold text-gray-800"
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {i18next.t('companion.promo.download')}
                </a>
                <button
                    className="hover:darken-20 rounded-sm px-2 py-1 font-bold"
                    onClick={dismiss}
                    type="button"
                >
                    {i18next.t('dismiss')}
                </button>
            </div>
        </div>
    );
};

export default InstallDesktopAppBanner;
